import React from "react";
import { ModuleConfig } from "../../types/ModuleConfig";

// Import mechanic jobs components (lazy-loaded for performance)
const Dashboard = React.lazy(() => import("./Dashboard"));
const JobsList = React.lazy(() => import("./JobsList"));
const NewJob = React.lazy(() => import("./NewJob"));
const JobDetails = React.lazy(() => import("./JobDetails"));
const Vehicles = React.lazy(() => import("./Vehicles"));
const VehicleDetails = React.lazy(() => import("./VehicleDetails"));
const PartsOrdersList = React.lazy(() => import("./PartsOrdersList"));
const MechanicView = React.lazy(() => import("./MechanicView"));
const FinancialActivities = React.lazy(() => import("./FinancialActivities"));
const Reports = React.lazy(() => import("./Reports"));

/**
 * Mechanic Jobs Module Config
 *
 * SINGLE SOURCE OF TRUTH:
 * Permissions are defined ONCE in the backend (mechanic-jobs/permissions.ts)
 * and fetched via the modulePermissions GraphQL query.
 *
 * Routes and nav items use `permissionLevel` (e.g. "view", "create", "edit", "financial")
 * which gets resolved to actual permission strings at runtime from the backend.
 *
 * To change permissions: Edit business-builder-backend/src/modules/mechanic-jobs/permissions.ts
 * That's the only place. The frontend picks it up automatically.
 */
const mechanicJobsModuleConfig: ModuleConfig = {
    // MODULE IDENTITY
    id: "mechanic-jobs",
    name: "Mechanic Jobs",
    description: "Mobile mechanic job tracking, parts ordering, and vehicle repair management",
    version: "1.0.0",
    enabled: true,
    category: "operations",
    order: 15,
    icon: "FiTruck",
    color: "#DD6B20",

    // ROUTES - permissionLevel is resolved from backend at runtime
    routes: [
        {
            path: "/mechanic-jobs",
            component: Dashboard,
            permissionLevel: "view",
        },
        {
            path: "/mechanic-jobs/jobs",
            component: JobsList,
            permissionLevel: "view",
        },
        {
            path: "/mechanic-jobs/jobs/new",
            component: NewJob,
            permissionLevel: "create",
        },
        {
            path: "/mechanic-jobs/jobs/:id",
            component: JobDetails,
            permissionLevel: "view",
        },
        {
            path: "/mechanic-jobs/vehicles",
            component: Vehicles,
            permissionLevel: "view",
        },
        {
            path: "/mechanic-jobs/vehicles/:id",
            component: VehicleDetails,
            permissionLevel: "view",
        },
        {
            path: "/mechanic-jobs/parts-orders",
            component: PartsOrdersList,
            permissionLevel: "view",
        },
        {
            path: "/mechanic-jobs/financial",
            component: FinancialActivities,
            permissionLevel: "financial",
        },
        {
            path: "/mechanic-jobs/reports",
            component: Reports,
            permissionLevel: "view",
        },
        {
            path: "/mechanic-jobs/m/:jobId",
            component: MechanicView,
            permissions: [], // Public route - mechanic access via phone number
        }
    ],

    // NAVIGATION - permissionLevel is resolved from backend at runtime
    navigation: [
        {
            label: "Dashboard",
            path: "/mechanic-jobs",
            icon: "🏠",
            permissionLevel: "view",
        },
        {
            label: "All Jobs",
            path: "/mechanic-jobs/jobs",
            icon: "📋",
            permissionLevel: "view",
        },
        {
            label: "New Job",
            path: "/mechanic-jobs/jobs/new",
            icon: "➕",
            permissionLevel: "create",
        },
        {
            label: "Vehicles",
            path: "/mechanic-jobs/vehicles",
            icon: "🚐",
            permissionLevel: "view",
        },
        {
            label: "Parts Orders",
            path: "/mechanic-jobs/parts-orders",
            icon: "📦",
            permissionLevel: "view",
        },
        {
            label: "Financial",
            path: "/mechanic-jobs/financial",
            icon: "💰",
            permissionLevel: "financial",
        },
        {
            label: "Reports",
            path: "/mechanic-jobs/reports",
            icon: "📊",
            permissionLevel: "view",
        }
    ],

    // Permissions come from the backend (single source of truth).
    // See: business-builder-backend/src/modules/mechanic-jobs/permissions.ts
    // These empty arrays get replaced at runtime by ModulePermissionsContext.
    permissions: {
        view: [],
        create: [],
        edit: [],
        delete: [],
        financial: [],
    },

    // QUICK ACTIONS
    quickActions: [
        {
            label: "Create Job",
            path: "/mechanic-jobs/jobs/new",
            icon: "➕",
            description: "Create a new mechanic job"
        },
        {
            label: "Add Vehicle",
            path: "/mechanic-jobs/vehicles?action=new",
            icon: "🚐",
            description: "Add a new vehicle to the system"
        }
    ],

    // CHANGELOG
    changelog: [
        {
            version: "1.0.0",
            releaseDate: "2026-01-27",
            title: "Initial Release - Mobile Mechanic Jobs",
            features: [
                "Job management with status workflow (Draft -> Awaiting Parts -> Scheduled -> In Progress -> Completed)",
                "Vehicle management with rego lookup",
                "Parts ordering with receipt uploads",
                "Mechanic public access via phone number URL",
                "Financial activity tagging for accountant",
                "Timeline and progress notes",
                "Checklists with completion tracking",
                "Reports and analytics"
            ],
            notes: "Initial release of the Mobile Mechanic Jobs module for Tom Miller Services."
        }
    ],

    // ROADMAP
    roadmap: [
        {
            title: "Rego Lookup Integration",
            description: "Integration with O-Parts or ProLink API for automatic vehicle parts lookup by registration",
            targetVersion: "1.1.0",
            priority: "high",
            status: "planned",
            features: [
                "Enter rego -> get vehicle details",
                "Auto-populate vehicle info",
                "Parts catalogue integration"
            ]
        },
        {
            title: "SMS Notifications",
            description: "Automated SMS notifications for job status updates",
            targetVersion: "1.2.0",
            priority: "medium",
            status: "planned",
            features: [
                "Parts ordered notification",
                "Parts delivered notification",
                "Job complete notification"
            ]
        }
    ]
};

export default mechanicJobsModuleConfig;
